import { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Clock, BookOpen } from 'lucide-react';
import { getActiveAlerts, getAlertHistory, getPlaybook, acknowledgeAlertAction, resolveAlertAction } from '../../../services/firestore/opsService';
import type { OpsAlert, IncidentPlaybook } from '../../../types/ops';

const C = {
  text: '#18181B', textSec: '#52525B', textMuted: '#A1A1AA',
  rose: '#E11D48', pass: '#059669', warn: '#B45309',
  bgCard: '#FFFFFF', border: '#E4E4E7',
};

const SEV_COLORS: Record<string, { color: string; bg: string }> = {
  P0: { color: '#DC2626', bg: '#FEF2F2' },
  P1: { color: '#EA580C', bg: '#FFF7ED' },
  P2: { color: '#B45309', bg: '#FEF3C7' },
  P3: { color: '#71717A', bg: '#F4F4F5' },
};

type Filter = 'all' | 'active' | 'acknowledged' | 'resolved';

export function AlertsTab() {
  const [active, setActive] = useState<OpsAlert[]>([]);
  const [history, setHistory] = useState<OpsAlert[]>([]);
  const [playbook, setPlaybook] = useState<IncidentPlaybook | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, h, p] = await Promise.all([getActiveAlerts(), getAlertHistory(20), getPlaybook()]);
        setActive(a);
        setHistory(h);
        setPlaybook(p);
      } catch (err) {
        console.error('Failed to load alerts:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refreshAlerts = async () => {
    const [a, h] = await Promise.all([getActiveAlerts(), getAlertHistory(20)]);
    setActive(a);
    setHistory(h);
  };

  const handleAcknowledge = async (alertId: string) => {
    setActionLoading(alertId);
    try {
      await acknowledgeAlertAction(alertId);
      await refreshAlerts();
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolve = async (alertId: string) => {
    setActionLoading(alertId);
    try {
      await resolveAlertAction(alertId);
      await refreshAlerts();
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: C.textMuted }}>Loading alerts...</div>;

  const allAlerts = [
    ...active.map((a) => ({ ...a, lifecycle: a.acknowledgedAt ? 'acknowledged' : 'active' })),
    ...history.map((a) => ({ ...a, lifecycle: 'resolved' as const })),
  ];

  const filtered = filter === 'all' ? allAlerts : allAlerts.filter((a) => a.lifecycle === filter);

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['all', 'active', 'acknowledged', 'resolved'] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '6px 14px', fontSize: 13, borderRadius: 4, border: `1px solid ${C.border}`,
              background: filter === f ? C.rose : C.bgCard,
              color: filter === f ? '#fff' : C.textSec,
              cursor: 'pointer', fontWeight: filter === f ? 600 : 400,
            }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'active' && active.length > 0 && ` (${active.length})`}
          </button>
        ))}
      </div>

      {/* Alert table */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.textMuted }}>
            <CheckCircle2 size={24} style={{ marginBottom: 8 }} />
            <p style={{ fontSize: 14 }}>No alerts matching this filter.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Severity</Th>
                <Th>Title</Th>
                <Th>Category</Th>
                <Th>Detected</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((alert) => {
                const sev = SEV_COLORS[alert.severity] || SEV_COLORS.P3;
                return (
                  <tr key={alert.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        fontSize: 12, fontWeight: 700, color: sev.color, background: sev.bg,
                        padding: '2px 8px', borderRadius: 4,
                      }}>
                        {alert.severity}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <p style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{alert.title}</p>
                      <p style={{ fontSize: 13, color: C.textSec, marginTop: 2 }}>{alert.description}</p>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: C.textSec }}>{alert.category}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: C.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
                      {new Date(alert.detectedAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {alert.lifecycle === 'active' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.rose }}>
                          <AlertTriangle size={12} /> Active
                        </span>
                      )}
                      {alert.lifecycle === 'acknowledged' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.warn }}>
                          <Clock size={12} /> Acknowledged
                        </span>
                      )}
                      {alert.lifecycle === 'resolved' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.pass }}>
                          <CheckCircle2 size={12} /> Resolved
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {alert.lifecycle === 'active' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => handleAcknowledge(alert.id)}
                            disabled={actionLoading === alert.id}
                            style={{
                              fontSize: 12, padding: '4px 10px', borderRadius: 4,
                              border: `1px solid ${C.border}`, background: C.bgCard,
                              color: C.warn, cursor: 'pointer', fontWeight: 600,
                              opacity: actionLoading === alert.id ? 0.5 : 1,
                            }}
                          >
                            Ack
                          </button>
                          <button
                            onClick={() => handleResolve(alert.id)}
                            disabled={actionLoading === alert.id}
                            style={{
                              fontSize: 12, padding: '4px 10px', borderRadius: 4,
                              border: `1px solid ${C.border}`, background: C.bgCard,
                              color: C.pass, cursor: 'pointer', fontWeight: 600,
                              opacity: actionLoading === alert.id ? 0.5 : 1,
                            }}
                          >
                            Resolve
                          </button>
                        </div>
                      )}
                      {alert.lifecycle === 'acknowledged' && (
                        <button
                          onClick={() => handleResolve(alert.id)}
                          disabled={actionLoading === alert.id}
                          style={{
                            fontSize: 12, padding: '4px 10px', borderRadius: 4,
                            border: `1px solid ${C.border}`, background: C.bgCard,
                            color: C.pass, cursor: 'pointer', fontWeight: 600,
                            opacity: actionLoading === alert.id ? 0.5 : 1,
                          }}
                        >
                          Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Incident Response Playbook */}
      {playbook && (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20, marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <BookOpen size={16} style={{ color: C.rose }} />
            <p style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: C.textMuted }}>
              Incident Response Playbook
            </p>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Severity</Th>
                <Th>First Responder</Th>
                <Th>Steps</Th>
              </tr>
            </thead>
            <tbody>
              {playbook.entries.map((entry) => (
                <tr key={entry.severity} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: SEV_COLORS[entry.severity]?.color || C.text }}>
                    {entry.severity}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 14, color: C.text }}>{entry.firstResponder}</td>
                  <td style={{ padding: '10px 12px', fontSize: 13, color: C.textSec }}>
                    {entry.steps.map((s, i) => <div key={i}>{i + 1}. {s}</div>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{
      textAlign: 'left', fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: 0.5, color: '#A1A1AA', padding: '8px 12px', borderBottom: '1px solid #E4E4E7',
    }}>
      {children}
    </th>
  );
}
